import { savedBots } from "../config/defaults";
import type { BotProfile, BotsConfig } from "../config/types";
import { countLeafChanges, isEqual } from "../../projects/config/utils";

/**
 * The ten subsections of a project's bot settings, in the order the Settings tab
 * draws them — and the order the left rail lists them, because the rail IS
 * this list. One source, so a card and its rail entry cannot disagree about
 * their name, their anchor or their modified dot.
 *
 * The grouping is by the LAYER of the cold-start solution rather than by the
 * tables behind it: where bots go, what a living player experiences, the
 * background chatter, and last how the whole thing retires itself. It is the
 * order the single card on the Config page already used; what changed is that
 * each group is now a card of its own with an anchor to link at.
 *
 * `enabled` belongs to no section: the Bots page header carries that switch and
 * writes it straight to the server. `bots.names` and `bots.profile`
 * belong to no section either — they answer "where does a bot come from", and
 * they live on the Source tab.
 */
export type BotsSectionID =
  | "population"
  | "locales"
  | "backfill"
  | "help"
  | "newcomer"
  | "behaviour"
  | "progression"
  | "limits"
  | "leaderboards"
  | "maturation";

export interface BotsSectionMeta {
  /** DOM id of the card — the rail's anchor and the scroll-spy target. */
  id: BotsSectionID;
  /** The card header. */
  title: string;
  /**
   * The rail entry, when the title is too long for a 240px column. Three of
   * the ten titles name two things ("Help & guaranteed response") because the
   * card holds two; the rail only has to be recognisable, and a truncated
   * label with an ellipsis in it is less recognisable than a short one.
   */
  railLabel?: string;
  /** Mono sub-label in the card header, and in the rail's tooltip. */
  apiName: string;
  /**
   * The `bots` keys this section owns. What lights its modified dot, and what
   * the save bar counts its changes over — a section is exactly the keys it
   * can edit, so nothing on the page can be dirty with no dot to show for it.
   */
  keys: readonly (keyof BotsConfig)[];
  /**
   * The part of a key this section edits, when another section edits the rest
   * of it. `bots.profiles[]` is one table with two owners — Behaviour the
   * rows, their shares and chatter, Progression each row's `play` — and an
   * edit to a rhythm must light Progression's dot, not Behaviour's.
   *
   * A view sees the other side of the comparison too (`other`: the server
   * snapshot when it is given the draft, and the other way round), so a
   * section can leave out what only one side has.
   */
  views?: Partial<
    Record<keyof BotsConfig, (value: unknown, other: unknown) => unknown>
  >;
  /**
   * Config paths below `keys` that belong to this section rather than to the
   * key's owner, with `[]` for any index (`profiles[].play`). What
   * `botsSectionFor` sends a link to.
   */
  paths?: readonly string[];
}

/** What of `key` the section is answerable for — the whole value, or its view. */
function sectionValue(
  section: BotsSectionMeta,
  key: keyof BotsConfig,
  bots: BotsConfig,
  other: BotsConfig,
): unknown {
  const view = section.views?.[key];
  return view ? view(bots[key], other[key]) : bots[key];
}

/**
 * Each row's `play` by profile key, for the keys BOTH sides hold. Adding,
 * removing or renaming a profile is Behaviour's change; Progression only
 * answers for the rhythm of a profile that was there before and still is.
 */
function playByKey(rows: unknown, other: unknown): Record<string, unknown> {
  const theirs = new Set((other as BotProfile[]).map((r) => r.key));
  const mine = new Map<string, unknown>();
  for (const r of rows as BotProfile[]) {
    if (theirs.has(r.key) && !mine.has(r.key)) mine.set(r.key, r.play);
  }
  // Sorted, so the two sides compare equal whatever order their rows are in.
  return Object.fromEntries([...mine].sort(([x], [y]) => x.localeCompare(y)));
}

export const BOTS_SECTIONS: readonly BotsSectionMeta[] = [
  { id: "population", title: "Population", apiName: "bots.population", keys: ["population"] },
  {
    id: "locales",
    title: "Locales",
    apiName: "bots.default_locale · bots.locale_by_country",
    keys: ["default_locale", "locale_by_country"],
  },
  { id: "backfill", title: "Chat backfill", apiName: "bots.backfill", keys: ["backfill"] },
  {
    id: "help",
    title: "Help & guaranteed response",
    railLabel: "Help",
    apiName: "bots.help",
    keys: ["help"],
  },
  {
    id: "newcomer",
    title: "Newcomer boost",
    apiName: "bots.newcomer_boost",
    keys: ["newcomer_boost"],
  },
  {
    id: "behaviour",
    title: "Behaviour profiles & time of day",
    railLabel: "Behaviour",
    apiName: "bots.profiles[] · bots.time_of_day[] · bots.simulation",
    keys: ["profiles", "time_of_day", "simulation"],
    views: {
      profiles: (rows) =>
        (rows as BotProfile[]).map(({ play: _play, ...rest }) => rest),
    },
  },
  {
    id: "progression",
    title: "Progression",
    apiName: "bots.profiles[].play · bots.growth[]",
    keys: ["profiles", "growth"],
    views: { profiles: playByKey },
    paths: ["profiles[].play"],
  },
  {
    id: "limits",
    title: "Guild limits & chat reactions",
    railLabel: "Guild limits",
    apiName: "bots.guild_limits · bots.chat",
    keys: ["guild_limits", "chat"],
  },
  {
    id: "leaderboards",
    title: "Leaderboards",
    apiName: "bots.leaderboards[]",
    keys: ["leaderboards"],
  },
  {
    id: "maturation",
    title: "Maturation",
    apiName: "bots.maturation",
    keys: ["maturation"],
  },
];

/**
 * The section with this id.
 *
 * Every card of the Settings tab is wired to its own meta through this rather
 * than through `BOTS_SECTIONS[n]`: the list above is explicitly an ORDER, with
 * an invitation to change it, and a reordering that silently moved a card's
 * modified dot onto its neighbour's keys is a bug nothing would catch.
 */
export function botsSection(id: BotsSectionID): BotsSectionMeta {
  const found = BOTS_SECTIONS.find((s) => s.id === id);
  if (!found) throw new Error(`bots section «${id}» is not in BOTS_SECTIONS`);
  return found;
}

/**
 * botsSectionFor answers which card owns a `bots.…` config path — the half of
 * a link out of Overview that says WHERE to scroll. Unknown paths (`bots.names`
 * and `bots.profile`, which live on the Source tab) return null, and the caller
 * links at the tab without an anchor rather than at a card that is not there.
 */
export function botsSectionFor(path: string): BotsSectionID | null {
  const rest = path.replace(/^bots\./, "").replace(/\[\d*\]/g, "[]");
  const claimed = BOTS_SECTIONS.find((s) =>
    (s.paths ?? []).some((p) => rest === p || rest.startsWith(`${p}.`)),
  );
  if (claimed) return claimed.id;
  const key = rest.split(".")[0].replace(/\[\]$/, "");
  const found = BOTS_SECTIONS.find((s) =>
    (s.keys as readonly string[]).includes(key),
  );
  return found ? found.id : null;
}

/** Was this subsection edited against the server snapshot? */
export function isBotsSectionDirty(
  server: BotsConfig,
  working: BotsConfig,
  section: BotsSectionMeta,
): boolean {
  // Compared as Save would send them, so the dot agrees with the patch.
  const a = savedBots(server);
  const b = savedBots(working);
  return section.keys.some(
    (key) =>
      !isEqual(sectionValue(section, key, a, b), sectionValue(section, key, b, a)),
  );
}

/**
 * What the save bar says: leaf changes and how many subsections hold them,
 * counted with the config page's own walker so the two bars agree on what
 * "one change" is.
 */
export function countBotsChanges(
  server: BotsConfig,
  working: BotsConfig,
): { changes: number; sections: number } {
  // Counted as Save would send them, like isBotsSectionDirty.
  const a = savedBots(server);
  const b = savedBots(working);
  let changes = 0;
  let sections = 0;
  for (const section of BOTS_SECTIONS) {
    let n = 0;
    for (const key of section.keys) {
      n += countLeafChanges(
        sectionValue(section, key, a, b),
        sectionValue(section, key, b, a),
      );
    }
    if (n > 0) {
      sections += 1;
      changes += n;
    }
  }
  return { changes, sections };
}
